import {
  Box,
  Icon,
  Input,
  MultiSelect,
  Select,
  Stack,
  Table,
  TableColumns,
  useToast,
  Toggle,
  Spinner,
  Fieldset,
} from '@sixfold/common-ui';
import { exhaustiveCheck, isNil, notNil } from '@sixfold/typed-primitives';
import React, { useCallback, useEffect, useReducer, useState } from 'react';

import { Button } from '../../components/button';
import {
  CompanyConfigurationQuery,
  CompanyCustomFieldConfiguration,
  CompanyCustomFieldConfigurationInput,
  ConfigurationUpdateInput,
  CustomFieldConfigurationVisibility,
  CustomFieldConfigurationVisibilityAction,
  TransportIdentifier,
  UpdateCompanyConfigurationInput,
  UpdateCompanySettingsInput,
  CompanySettingsInput,
} from '../../lib/graphql';

import styles from './styles.module.css';

export interface CompanyConfigurationViewProps {
  data: CompanyConfigurationQuery;
  updateConfiguration: (input: UpdateCompanyConfigurationInput) => Promise<void>;
  updateSetting: (input: UpdateCompanySettingsInput) => Promise<void>;
}

const transportIdentifierOptions = [
  { value: TransportIdentifier.DELIVERY_NUMBER, label: TransportIdentifier.DELIVERY_NUMBER },
  { value: TransportIdentifier.ORDER_NUMBER, label: TransportIdentifier.ORDER_NUMBER },
  { value: TransportIdentifier.PURCHASE_ORDER_NUMBER, label: TransportIdentifier.PURCHASE_ORDER_NUMBER },
];

const sharedStopViewTransportIdentifierOptions = [
  { value: TransportIdentifier.TRANSPORT_NUMBER, label: TransportIdentifier.TRANSPORT_NUMBER },
  ...transportIdentifierOptions,
];

const visibilityOptions = [
  { value: CustomFieldConfigurationVisibility.CREATOR, label: CustomFieldConfigurationVisibility.CREATOR },
  { value: CustomFieldConfigurationVisibility.EVERYONE, label: CustomFieldConfigurationVisibility.EVERYONE },
  {
    value: CustomFieldConfigurationVisibility.FORWARDING_CHAIN,
    label: CustomFieldConfigurationVisibility.FORWARDING_CHAIN,
  },
];

const visibilityActionOptions = [
  {
    value: CustomFieldConfigurationVisibilityAction.FALLBACK,
    label: CustomFieldConfigurationVisibilityAction.FALLBACK,
  },
];

type CustomFieldRow = Omit<CompanyCustomFieldConfiguration, '__typename'> & {
  id: string;
};

type CustomFieldsAction =
  | { type: 'add' }
  | { type: 'update'; id: string; updates: Partial<Omit<CustomFieldRow, 'id'>> }
  | { type: 'delete'; id: string }
  | { type: 'reset'; fields: CompanyCustomFieldConfiguration[] };

function customFieldsReducer(state: CustomFieldRow[], action: CustomFieldsAction): CustomFieldRow[] {
  switch (action.type) {
    case 'add':
      return [
        ...state,
        {
          id: `field-${Date.now()}`,
          key: '',
          displayName: null,
          visibility: null,
          visibilityAction: null,
        },
      ];
    case 'update':
      return state.map((row) => (row.id === action.id ? { ...row, ...action.updates } : row));
    case 'delete':
      return state.filter((row) => row.id !== action.id);
    case 'reset':
      return action.fields.map((field) => ({
        id: field.key,
        key: field.key,
        displayName: field.displayName ?? null,
        visibility: field.visibility ?? null,
        visibilityAction: field.visibilityAction ?? null,
      }));
    default:
      exhaustiveCheck(action);
  }
}

const customFieldColumns: TableColumns<CustomFieldRow, { dispatch: React.Dispatch<CustomFieldsAction> }> = [
  {
    id: 'key',
    title: 'Key',
    cellContent: ({ row, context }) => (
      <Input
        label="Key"
        labelPosition="hidden"
        name={`custom-field-key-${row.id}`}
        value={row.key}
        placeholder="Enter custom field key"
        onChange={({ value }) => context.dispatch({ type: 'update', id: row.id, updates: { key: value } })}
      />
    ),
  },
  {
    id: 'visibility',
    title: 'Visibility',
    cellContent: ({ row, context }) => (
      <Select
        isClearable
        placeholder="Select visibility"
        options={visibilityOptions}
        value={row.visibility ?? null}
        onChange={(option) =>
          context.dispatch({
            type: 'update',
            id: row.id,
            updates: {
              visibility: option === null ? undefined : (option.value as CustomFieldConfigurationVisibility),
            },
          })
        }
      />
    ),
  },
  {
    id: 'visibilityAction',
    title: 'Visibility Action',
    cellContent: ({ row, context }) => (
      <Select
        isClearable
        placeholder="Select action"
        options={visibilityActionOptions}
        value={row.visibilityAction ?? null}
        onChange={(option) =>
          context.dispatch({
            type: 'update',
            id: row.id,
            updates: {
              visibilityAction:
                option === null ? undefined : (option.value as CustomFieldConfigurationVisibilityAction),
            },
          })
        }
      />
    ),
  },
  {
    id: 'displayName',
    title: 'Display Name',
    cellContent: ({ row, context }) => (
      <Input
        label="Display Name"
        labelPosition="hidden"
        name={`custom-field-display-name-${row.id}`}
        value={row.displayName ?? ''}
        placeholder="Enter display name"
        onChange={({ value }) =>
          context.dispatch({
            type: 'update',
            id: row.id,
            updates: { displayName: value.trim() === '' ? null : value },
          })
        }
      />
    ),
  },
  {
    id: 'id',
    title: 'Actions',
    cellContent: ({ value, context }) => (
      <span
        onClick={() => context.dispatch({ type: 'delete', id: value })}
        style={{ cursor: 'pointer', color: 'red', display: 'inline-flex', alignItems: 'center', height: '100%' }}>
        <Icon name="small-bin" />
      </span>
    ),
  },
];

const CustomFieldsTable: React.FC<{
  rows: CustomFieldRow[];
  dispatch: React.Dispatch<CustomFieldsAction>;
}> = ({ rows, dispatch }) => <Table data={rows} columns={customFieldColumns} context={{ dispatch }} />;

export const CompanyConfigurationView: React.FC<CompanyConfigurationViewProps> = (props) => {
  const configurations = props.data.company?.configuration?.configurations;
  const settings = props.data.company?.settings;
  const notify = useToast();

  const [customerPortalPrimaryTI, setCustomerPortalPrimaryTI] = useState<TransportIdentifier>(
    configurations?.customerPortal?.primaryTransportIdentifier ?? TransportIdentifier.TRANSPORT_NUMBER,
  );

  const [tourMinAbandonTime, setTourMinAbandonTime] = useState(settings?.tourMinAbandonTimeHours);
  useEffect(() => {
    setTourMinAbandonTime(settings?.tourMinAbandonTimeHours);
  }, [settings?.tourMinAbandonTimeHours]);

  const [tourMaxAbandonTime, setTourMaxAbandonTime] = useState(settings?.tourMaxAbandonTimeHours);
  useEffect(() => {
    setTourMaxAbandonTime(settings?.tourMaxAbandonTimeHours);
  }, [settings?.tourMaxAbandonTimeHours]);
  const [showInvalidAbandonTimeRange, setShowInvalidAbandonTimeRange] = useState(false);

  const [isMutatingAllocationPreference, setIsMutatingAllocationPreference] = useState(false);

  useEffect(() => {
    setCustomerPortalPrimaryTI(
      configurations?.customerPortal?.primaryTransportIdentifier ?? TransportIdentifier.TRANSPORT_NUMBER,
    );
  }, [configurations?.customerPortal?.primaryTransportIdentifier]);

  const [customerPortalDetailViewTIs, setCustomerPortalDetailViewTIs] = useState<TransportIdentifier[]>(
    configurations?.customerPortal?.detailViewTransportIdentifiers ?? [],
  );

  useEffect(() => {
    setCustomerPortalDetailViewTIs(configurations?.customerPortal?.detailViewTransportIdentifiers ?? []);
  }, [configurations?.customerPortal?.detailViewTransportIdentifiers]);

  const [sharedStopViewVisibleTIs, setSharedStopViewVisibleTIs] = useState<TransportIdentifier[]>(
    configurations?.sharedStopView?.visibleTransportIdentifiers ?? [
      TransportIdentifier.DELIVERY_NUMBER,
      TransportIdentifier.PURCHASE_ORDER_NUMBER,
      TransportIdentifier.ORDER_NUMBER,
    ],
  );

  useEffect(() => {
    setSharedStopViewVisibleTIs(
      configurations?.sharedStopView?.visibleTransportIdentifiers ?? [
        TransportIdentifier.DELIVERY_NUMBER,
        TransportIdentifier.PURCHASE_ORDER_NUMBER,
        TransportIdentifier.ORDER_NUMBER,
      ],
    );
  }, [configurations?.sharedStopView?.visibleTransportIdentifiers]);

  const [sharedTourViewVisibleTIs, setSharedTourViewVisibleTIs] = useState<TransportIdentifier[]>(
    configurations?.sharedTourView?.visibleTransportIdentifiers ?? [],
  );

  useEffect(() => {
    setSharedTourViewVisibleTIs(configurations?.sharedTourView?.visibleTransportIdentifiers ?? []);
  }, [configurations?.sharedTourView?.visibleTransportIdentifiers]);

  const [customFieldsRows, dispatch] = useReducer(
    customFieldsReducer,
    configurations?.customFields?.fields ?? [],
    (fields) =>
      fields.map((field) => ({
        id: field.key,
        key: field.key,
        displayName: field.displayName ?? null,
        visibility: field.visibility ?? null,
        visibilityAction: field.visibilityAction ?? null,
      })),
  );

  useEffect(() => {
    const fields = configurations?.customFields?.fields ?? [];
    dispatch({ type: 'reset', fields });
  }, [configurations?.customFields?.fields]);

  const mapCustomFieldsInput = useCallback(
    (customFields: CustomFieldRow[]): { fields: CompanyCustomFieldConfigurationInput[] } | undefined => {
      if (customFields.length === 0) {
        return undefined;
      }
      return {
        fields: customFields.map((field) => ({
          key: field.key.trim(),
          displayName: field.displayName?.trim() ?? undefined,
          visibility: field.visibility ?? undefined,
          visibilityAction: field.visibilityAction ?? undefined,
        })),
      };
    },
    [],
  );

  const updateConfiguration = useCallback(
    async (partialConfiguration: ConfigurationUpdateInput & CompanySettingsInput) => {
      if (isNil(props.data.company)) {
        notify.danger('Unable to save configuration: company is missing', { autoClose: 'short' });
        return;
      }

      if ('allocationPreferTractorOverTrailer' in partialConfiguration) {
        try {
          setIsMutatingAllocationPreference(true);
          await props.updateSetting({
            companyId: props.data.company.company_id,
            settings: {
              allocationPreferTractorOverTrailer: partialConfiguration.allocationPreferTractorOverTrailer,
            },
          });
        } finally {
          setIsMutatingAllocationPreference(false);
        }
      } else if (
        'tourMinAbandonTimeHours' in partialConfiguration ||
        'tourMaxAbandonTimeHours' in partialConfiguration
      ) {
        await props.updateSetting({
          companyId: props.data.company.company_id,
          settings: {
            tourMinAbandonTimeHours: partialConfiguration.tourMinAbandonTimeHours,
            tourMaxAbandonTimeHours: partialConfiguration.tourMaxAbandonTimeHours,
          },
        });
      } else {
        await props.updateConfiguration({
          companyId: props.data.company.company_id,
          configuration: {
            customFields: partialConfiguration.customFields ?? mapCustomFieldsInput(customFieldsRows),
            customerPortal: partialConfiguration.customerPortal,
            sharedStopView: partialConfiguration.sharedStopView,
            sharedTourView: partialConfiguration.sharedTourView,
          },
        });
      }

      const updatedParts: string[] = [];
      if (partialConfiguration.customerPortal !== undefined) updatedParts.push('Customer portal');
      if (partialConfiguration.sharedStopView !== undefined) updatedParts.push('Shared stop view');
      if (partialConfiguration.sharedTourView !== undefined) updatedParts.push('Shared tour view');
      if (partialConfiguration.customFields !== undefined) updatedParts.push('Custom fields');
      if (partialConfiguration.allocationPreferTractorOverTrailer !== undefined)
        updatedParts.push('Allocation preference');
      if (
        partialConfiguration.tourMinAbandonTimeHours !== undefined ||
        partialConfiguration.tourMaxAbandonTimeHours !== undefined
      )
        updatedParts.push('Tour abandon time');

      notify.success(`${updatedParts.join(', ')} configuration saved`, { autoClose: 'short' });
    },
    [customFieldsRows, props, mapCustomFieldsInput, notify],
  );

  return (
    <Stack direction="row" gap="medium">
      <Box style={{ flex: 1 }}>
        <Stack direction="column" gap="medium">
          <h2>Customer portal</h2>
          <Stack direction="row" gap="medium" alignItems="center">
            <Box style={{ flex: 1 }}>Primary transport identifier</Box>
            <Box style={{ flex: 1 }}>
              <Select
                options={Object.keys(TransportIdentifier).map((type) => ({
                  value: type,
                  label: type,
                }))}
                value={customerPortalPrimaryTI}
                onChange={(option) => {
                  if (option !== null) {
                    setCustomerPortalPrimaryTI(option.value as TransportIdentifier);
                  }
                }}
              />
            </Box>
          </Stack>
          <Stack direction="row" gap="medium" alignItems="center">
            <Box style={{ flex: 1 }}>Detail view transport identifiers</Box>
            <Box style={{ flex: 1 }}>
              <MultiSelect
                options={transportIdentifierOptions}
                value={customerPortalDetailViewTIs}
                onChange={(options) => {
                  setCustomerPortalDetailViewTIs(options.map((o) => o.value as TransportIdentifier));
                }}
              />
            </Box>
          </Stack>
          <Stack justifyContent="end">
            <Button
              type="button"
              size="small"
              onClick={() =>
                updateConfiguration({
                  customerPortal: {
                    value: undefined,
                  },
                })
              }>
              Reset to default
            </Button>
            <Button
              type="submit"
              kind="primary"
              size="small"
              disabled={isNil(customerPortalPrimaryTI) || customerPortalDetailViewTIs.length === 0}
              onClick={() =>
                updateConfiguration({
                  customerPortal: {
                    value: {
                      primaryTransportIdentifier: customerPortalPrimaryTI,
                      detailViewTransportIdentifiers: customerPortalDetailViewTIs,
                    },
                  },
                })
              }>
              Save
            </Button>
          </Stack>
          <h2>Shared Stop View</h2>
          <Stack direction="row" gap="medium" alignItems="center">
            <Box style={{ flex: 1 }}>Visible transport identifiers</Box>
            <Box style={{ flex: 1 }}>
              <MultiSelect
                options={sharedStopViewTransportIdentifierOptions}
                value={sharedStopViewVisibleTIs}
                onChange={(options) => {
                  setSharedStopViewVisibleTIs(options.map((o) => o.value as TransportIdentifier));
                }}
              />
            </Box>
          </Stack>
          <Stack justifyContent="end">
            <Button
              type="button"
              size="small"
              onClick={() =>
                updateConfiguration({
                  sharedStopView: {
                    value: undefined,
                  },
                })
              }>
              Reset to default
            </Button>
            <Button
              type="submit"
              kind="primary"
              size="small"
              disabled={sharedStopViewVisibleTIs.length === 0}
              onClick={() =>
                updateConfiguration({
                  sharedStopView: {
                    value: {
                      visibleTransportIdentifiers: sharedStopViewVisibleTIs,
                    },
                  },
                })
              }>
              Save
            </Button>
          </Stack>
          <h2>Shared Tour View</h2>
          <Stack direction="row" gap="medium" alignItems="center">
            <Box style={{ flex: 1 }}>Visible transport identifiers</Box>
            <Box style={{ flex: 1 }}>
              <MultiSelect
                options={transportIdentifierOptions}
                value={sharedTourViewVisibleTIs}
                onChange={(options) => {
                  setSharedTourViewVisibleTIs(options.map((o) => o.value as TransportIdentifier));
                }}
              />
            </Box>
          </Stack>
          <Stack justifyContent="end">
            <Button
              type="button"
              size="small"
              onClick={() =>
                updateConfiguration({
                  sharedTourView: {
                    value: undefined,
                  },
                })
              }>
              Reset to default
            </Button>
            <Button
              type="submit"
              kind="primary"
              size="small"
              disabled={sharedTourViewVisibleTIs.length === 0}
              onClick={() =>
                updateConfiguration({
                  sharedTourView: {
                    value: {
                      visibleTransportIdentifiers: sharedTourViewVisibleTIs,
                    },
                  },
                })
              }>
              Save
            </Button>
          </Stack>
          <Stack direction="column">
            <h2>Allocation preference</h2>
            <Toggle
              label={
                <Stack direction="row" alignItems="center" gap="small">
                  Prefer tractor over trailer (TTC transports)
                  {isMutatingAllocationPreference && <Spinner size="small" />}
                </Stack>
              }
              checked={settings?.allocationPreferTractorOverTrailer ?? false}
              onChange={({ checked }) => {
                void updateConfiguration({ allocationPreferTractorOverTrailer: checked });
              }}
            />
          </Stack>
          <Stack direction="column">
            <h2>Tour abandon time</h2>
            <Fieldset
              label="Minimum and/or maximum abandon time"
              labelClassName={styles.tourMinimumAbandonTimeLabel}
              labelPosition="inline"
              labelOffset="small"
              invalid={showInvalidAbandonTimeRange}
              errorMessage={showInvalidAbandonTimeRange ? 'Minimum cannot be higher than maximum' : undefined}>
              <Stack direction="row" alignItems="center">
                <Input
                  className={styles.tourMinimumAbandonTimeInput}
                  label="Minimum abandon time"
                  labelPosition="hidden"
                  name="tourMinAbandonTime"
                  size="small"
                  prefix="min"
                  suffix="hours"
                  invalid={showInvalidAbandonTimeRange}
                  value={tourMinAbandonTime === 0 ? '' : (tourMinAbandonTime?.toString() ?? '')}
                  onChange={({ value }) => {
                    const hours = value === '' ? 0 : Number(value);
                    if (hours >= 0) {
                      setTourMinAbandonTime(hours);
                    }
                  }}
                />
                <Input
                  className={styles.tourMinimumAbandonTimeInput}
                  label="Maximum abandon time"
                  labelPosition="hidden"
                  name="tourMaxAbandonTime"
                  size="small"
                  prefix="max"
                  suffix="hours"
                  invalid={showInvalidAbandonTimeRange}
                  value={tourMaxAbandonTime === 0 ? '' : (tourMaxAbandonTime?.toString() ?? '')}
                  onChange={({ value }) => {
                    const hours = value === '' ? 0 : Number(value);
                    if (hours >= 0) {
                      setTourMaxAbandonTime(hours);
                    }
                  }}
                />
                <Button
                  type="button"
                  size="small"
                  onClick={() => {
                    setShowInvalidAbandonTimeRange(false);
                    return updateConfiguration({
                      tourMinAbandonTimeHours: 0,
                      tourMaxAbandonTimeHours: 0,
                    });
                  }}>
                  Reset to default
                </Button>
                <Button
                  type="submit"
                  kind="primary"
                  size="small"
                  onClick={() => {
                    if (
                      notNil(tourMinAbandonTime) &&
                      notNil(tourMaxAbandonTime) &&
                      tourMaxAbandonTime > 0 &&
                      tourMinAbandonTime > tourMaxAbandonTime
                    ) {
                      setShowInvalidAbandonTimeRange(true);
                      return;
                    }
                    setShowInvalidAbandonTimeRange(false);
                    return updateConfiguration({
                      tourMinAbandonTimeHours: tourMinAbandonTime,
                      tourMaxAbandonTimeHours: tourMaxAbandonTime,
                    });
                  }}>
                  Save
                </Button>
              </Stack>
            </Fieldset>
          </Stack>
        </Stack>
      </Box>
      <Box style={{ flex: 1 }}>
        <Stack direction="column" gap="medium">
          <h2>Custom Fields Configuration</h2>
          <div>
            <CustomFieldsTable rows={customFieldsRows} dispatch={dispatch} />
            <Button type="button" size="small" iconStart="small-plus" onClick={() => dispatch({ type: 'add' })}>
              Add Custom Field
            </Button>
          </div>
          <Stack justifyContent="end">
            <Button
              type="submit"
              kind="primary"
              size="small"
              disabled={
                customFieldsRows.some((row) => row.key.trim() === '') ||
                customFieldsRows.some(
                  (row) => [row.visibilityAction, row.visibility, row.displayName].filter(notNil).length === 0,
                ) ||
                customFieldsRows.length !== new Set(customFieldsRows.map((c) => c.key.trim())).size
              }
              onClick={() =>
                updateConfiguration({
                  customFields: mapCustomFieldsInput(customFieldsRows),
                })
              }>
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};
